import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, tap, switchMap } from 'rxjs/operators';
import { API_BASE_URL } from '../config/app-settings.tokens';
import { RootFoldersResponse, BrowseFolderResponse } from '../models/file-management.model';
import { NotificationService } from './notification.service';

@Injectable({
  providedIn: 'root'
})
export class FileManagementService {
  private readonly baseUrl = `${inject(API_BASE_URL)}/api/v1/external`;
  private readonly filesUrl = `${inject(API_BASE_URL)}/getattachment/api/files`;
  private readonly loggingPrefix = '[FileManagementService]';

  constructor(
    private http: HttpClient,
    private notificationService: NotificationService
  ) {}

  /**
   * Get root folders with files for a user
   * GET /externalbackend/api/v1/external/folders/roots?iamUserId={iamUserId}&pageNumber={pageNumber}&pageSize={pageSize}
   * 
   * @param pageNumber Page number (default: 1)
   * @param pageSize Items per page (default: 10)
   * @returns Observable<RootFoldersResponse>
   */
  getRootFolders(pageNumber: number = 1, pageSize: number = 10): Observable<RootFoldersResponse> {
    return this.notificationService.ensureUserInitialized().pipe(
      switchMap((iamUserId) => {
        const params = new HttpParams()
          .set('iamUserId', iamUserId)
          .set('pageNumber', pageNumber.toString())
          .set('pageSize', pageSize.toString());

        return this.http.get<RootFoldersResponse>(
          `${this.baseUrl}/folders/roots`,
          { params }
        ).pipe(
          tap((response) => {
            this.handleResponseErrors(response.errors);
          }),
          catchError((error) => {
            return throwError(() => error);
          })
        );
      }),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Browse a specific folder with its subfolders and files
   * GET /externalbackend/api/v1/external/folders/{folderId}/browse?iamUserId={iamUserId}&pageNumber={pageNumber}&pageSize={pageSize}
   * 
   * @param folderId Folder ID to browse
   * @param pageNumber Page number (default: 1)
   * @param pageSize Items per page (default: 10)
   * @returns Observable<BrowseFolderResponse>
   */
  browseFolder(folderId: number, pageNumber: number = 1, pageSize: number = 10, keyword?: string | null): Observable<BrowseFolderResponse> {
    return this.notificationService.ensureUserInitialized().pipe(
      switchMap((iamUserId) => {
        let params = new HttpParams()
          .set('iamUserId', iamUserId)
          .set('pageNumber', pageNumber.toString())
          .set('pageSize', pageSize.toString());

        if (keyword !== undefined && keyword !== null && String(keyword).trim() !== '') {
          params = params.set('keyword', String(keyword));
        }

        return this.http.get<BrowseFolderResponse>(
          `${this.baseUrl}/folders/${folderId}/browse`,
          { params }
        ).pipe(
          tap((response) => {
            this.handleResponseErrors(response.errors);
          }),
          catchError((error) => {
            return throwError(() => error);
          })
        );
      }),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Handle API response errors
   * Display them as notifications if present
   */
  private handleResponseErrors(errors: string[] | null): void {
    if (errors && errors.length > 0) {
      errors.forEach((error) => {
      });
    }
  }

  /**
   * Open attachment file in a new tab
   * GET /api/files/attachment/{storageId}
   * 
   * @param storageId Storage ID of the file to open
   */
  // openAttachmentInNewTab(storageId: string): void {
  //   console.log(`${this.loggingPrefix} Opening attachment: ${storageId}`);
    
  //   this.http.get(
  //     `${this.filesUrl}/attachment/${storageId}`,
  //     { responseType: 'blob' }
  //   ).subscribe({
  //     next: (blob) => {
  //       const url = window.URL.createObjectURL(blob);
  //       window.open(url, '_blank');
  //       // Revoke after a short delay to allow the tab to open
  //       setTimeout(() => window.URL.revokeObjectURL(url), 100);
  //       console.log(`${this.loggingPrefix} Attachment opened successfully`);
  //     },
  //     error: (err) => {
  //       console.error(`${this.loggingPrefix} Failed to open attachment:`, err);
  //       // Note: Add error notification when NotificationService has a showError method
  //     }
  //   });
  // }
openAttachmentInNewTab(storageId: string): void {
  this.http.get(`${this.filesUrl}/attachment/${storageId}`, { 
    observe: 'response', // <--- Critical: Allows reading headers
    responseType: 'blob' 
  }).subscribe({
    next: (response: HttpResponse<Blob>) => {
      
      // 1. Read the Header
      const canDownloadHeader = response.headers.get('X-Can-Download');
      const canDownload = canDownloadHeader === 'true';
      const blob = response.body;

      if (!blob) return;

      const url = window.URL.createObjectURL(blob);

      if (canDownload) {

        window.open(url, '_blank');
        
        // Revoke URL after delay
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
      } else {

        // #toolbar=0 hides the top bar in Chrome/Edge, #navpanes=0 hides side bar
       // const viewerUrl = url + '#toolbar=0&navpanes=0&scrollbar=0';
        window.open(url, '_blank');
        
        // Revoke URL after delay to allow browser to render
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      }
    },
    error: (err) => {
      console.error('Failed to load file', err);
      // Handle error (e.g., show toast)
    }
  });
}

// Helper to extract filename
private getFileNameFromHeader(disposition: string | null): string | null {
  if (!disposition) return null;
  const result = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
  return result && result[1] ? result[1].replace(/['"]/g, '') : null;
}
}
